import { useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useDeleteFolder } from "hooks/api/use-folders";
import { CampaignFolderDTO } from "generated/schemas";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";
import { getErrorMessage } from "utilities";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { getCampaignPaths } from "utilities/app-routes";

export const useDeleteFolderModal = () => {
  const [deletingFolder, setDeletingFolder] = useState<CampaignFolderDTO | null>(null);
  const navigate = useNavigate();
  const { folderId: folderIdFromUrl } = useParams<{ folderId?: string }>();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();

  const deleteFolderMutation = useDeleteFolder();

  const openDeleteFolder = useCallback((folder: CampaignFolderDTO) => {
    setDeletingFolder(folder);
  }, []);

  const closeDeleteFolder = useCallback(() => {
    setDeletingFolder(null);
  }, []);

  const handleDeleteFolder = useCallback(() => {
    if (!deletingFolder?.id) return;

    deleteFolderMutation.mutate(deletingFolder.id, {
      onSuccess: () => {
        toast.success({
          data: {
            title: deletingFolder?.name ? `Deleted '${deletingFolder.name}'` : "Deleted folder",
          },
        });
        closeDeleteFolder();

        // If user is currently viewing this folder details page, exit to Creative list
        if (folderIdFromUrl && folderIdFromUrl === deletingFolder.id) {
          const { campaignCreativePath } = getCampaignPaths({
            clientId: currentClient.id,
            campaignId: currentCampaign.id,
          });
          navigate(campaignCreativePath, { replace: true });
        }
      },
      onError: (error) => {
        toast.error({
          data: {
            title: "Failed to delete folder",
            message: getErrorMessage(error),
          },
        });
      },
    });
  }, [
    deletingFolder,
    deleteFolderMutation,
    closeDeleteFolder,
    folderIdFromUrl,
    currentClient.id,
    currentCampaign.id,
    navigate,
  ]);

  const deleteFolderModalMarkup = useMemo(() => {
    if (!deletingFolder) return null;

    const isMutationPending = deleteFolderMutation.isPending;

    return (
      <ConfirmationDialog
        open
        onClose={closeDeleteFolder}
        onConfirm={handleDeleteFolder}
        intent="warning"
        headline="Are you sure you want to delete this folder?"
        message="All associated touchpoints will be moved to the ‘Not in a folder’ section."
        confirmButtonText="Delete folder"
        confirmLoadingMessage="Deleting folder..."
        confirming={isMutationPending}
      />
    );
  }, [deletingFolder, deleteFolderMutation.isPending, closeDeleteFolder, handleDeleteFolder]);

  return {
    openDeleteFolder,
    closeDeleteFolder,
    deleteFolderModalMarkup,
  };
};
